import { Avatar, Box, Chip, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TabBase, { TabResourceProps } from './TabBase';
import { RoomToSeasonEntity } from 'api/generated';
import { roomApi } from 'api';
import { H5, Tiny } from 'components/Typography';
import defaultProfileImage from '../../assets/images/default-profile.png';
import { useTheme } from '@mui/material/styles';
import { useSeason } from 'contexts/SeasonContext';
import FlexBox from 'components/flexbox/FlexBox';
import HotelIcon from '@mui/icons-material/Hotel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonIcon from '@mui/icons-material/Person';

export interface HousingFormData {
  id: number;
  name: string;
  buildingId: number;
  maxTenants: number;
  notes?: string;
  seasonNotes?: RoomToSeasonEntity[] | undefined;
}

interface RoomAssignment {
  id: number;
  employeeId: number;
  roomId: number;
  seasonId: number;
  startDate: string;
  endDate: string;
  bedIndex: number;
  notes: string;
  room?: { id: number; name: string; building?: { id: number; name: string } };
  employee?: { id: number; firstName: string; lastName: string; filePath?: string };
}

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const Housing: FC<TabResourceProps<HousingFormData> & { currentEmployeeId: number }> = ({
  currentEmployeeId,
  fetchEmployee,
}) => {
  const { seasonId } = useSeason();
  const { t } = useTranslation();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [myAssignments, setMyAssignments] = useState<RoomAssignment[]>([]);
  const [allAssignments, setAllAssignments] = useState<RoomAssignment[]>([]);
  const [seasonNote, setSeasonNote] = useState('');

  useEffect(() => {
    if (!currentEmployeeId || !seasonId) { setLoading(false); return; }
    setLoading(true);

    Promise.all([
      roomApi.findRoomAssignments(String(seasonId), String(currentEmployeeId)),
      roomApi.findRoomAssignments(String(seasonId), ''),
    ]).then(([myRes, allRes]) => {
      setMyAssignments((myRes.data as any[]) ?? []);
      setAllAssignments((allRes.data as any[]) ?? []);
    }).catch((err) => {
      console.error('Failed to load room assignments:', err);
    }).finally(() => setLoading(false));
  }, [currentEmployeeId, seasonId]);

  // Fetch room notes when we have an assignment
  useEffect(() => {
    const currentAssignment = myAssignments[0];
    if (currentAssignment?.roomId) {
      roomApi.getNotes(currentAssignment.roomId, seasonId).then(({ data }) => {
        setSeasonNote(data?.notes ?? '');
      }).catch(() => {});
    }
  }, [myAssignments, seasonId]);

  const navigateToEmployee = (id: number) => {
    if (fetchEmployee) fetchEmployee(id);
  };

  // Find roommates: other people assigned to the same room with overlapping dates
  const getRoommates = (assignment: RoomAssignment): RoomAssignment[] => {
    return allAssignments.filter(a =>
      a.roomId === assignment.roomId &&
      a.employeeId !== currentEmployeeId &&
      a.startDate < assignment.endDate &&
      a.endDate > assignment.startDate
    );
  };

  // Find next arrival: the next assignment in the same room that starts after the current one ends
  const getNextArrival = (assignment: RoomAssignment): RoomAssignment | undefined => {
    return allAssignments
      .filter(a =>
        a.roomId === assignment.roomId &&
        a.employeeId !== currentEmployeeId &&
        a.startDate >= assignment.endDate
      )
      .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
  };

  if (loading) {
    return (
      <TabBase title={t('housing.title')} setIsViewing={() => {}} isViewing dontShowMoveToNextToConfirm>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      </TabBase>
    );
  }

  if (myAssignments.length === 0) {
    return (
      <TabBase title={t('housing.title')} setIsViewing={() => {}} isViewing dontShowMoveToNextToConfirm>
        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
          <HotelIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
          <Typography color="text.secondary">{t('housing.noAssignment')}</Typography>
        </Box>
      </TabBase>
    );
  }

  return (
    <>
      {myAssignments.map((assignment) => {
        const roommates = getRoommates(assignment);
        const nextArrival = getNextArrival(assignment);
        const roomName = (assignment as any).room?.name ?? `${t('housing.room.label')} #${assignment.roomId}`;
        const buildingName = (assignment as any).room?.building?.name ?? '';

        return (
          <Box key={assignment.id}>
            <TabBase title={t('housing.title')} setIsViewing={() => {}} isViewing dontShowMoveToNextToConfirm>
              <Grid container spacing={2}>
                {/* Room & Building info */}
                <Grid item xs={12} sm={6}>
                  <FlexBox alignItems="center" gap={1} mb={1}>
                    <ApartmentIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{t('housing.itemName')}</Typography>
                  </FlexBox>
                  <Typography variant="body1" fontWeight={600}>{buildingName || '—'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FlexBox alignItems="center" gap={1} mb={1}>
                    <MeetingRoomIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{t('housing.room.label')}</Typography>
                  </FlexBox>
                  <FlexBox alignItems="center" gap={1}>
                    <Typography variant="body1" fontWeight={600}>{roomName}</Typography>
                    <Chip label={`${t('housing.bed')} ${assignment.bedIndex + 1}`} size="small" variant="outlined" />
                  </FlexBox>
                </Grid>

                {/* Dates */}
                <Grid item xs={12} sm={6}>
                  <FlexBox alignItems="center" gap={1} mb={1}>
                    <CalendarTodayIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{t('housing.period')}</Typography>
                  </FlexBox>
                  <Typography variant="body1">
                    {formatDate(assignment.startDate)} — {formatDate(assignment.endDate)}
                  </Typography>
                </Grid>

                {/* Notes */}
                {(assignment.notes || seasonNote) && (
                  <Grid item xs={12} sm={6}>
                    <FlexBox alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">{t('housing.notes.label')}</Typography>
                    </FlexBox>
                    {assignment.notes && <Typography variant="body2">{assignment.notes}</Typography>}
                    {seasonNote && <Tiny fontStyle="italic" mt={0.5}>{seasonNote}</Tiny>}
                  </Grid>
                )}
              </Grid>
            </TabBase>

            {/* Roommates & Next Arrival */}
            {(roommates.length > 0 || nextArrival) && (
              <TabBase
                titleComponent={
                  <FlexBox minWidth="100%" justifyContent="space-between">
                    <H5 padding={3}>{t('housing.assignedEmployees')}</H5>
                    {nextArrival && <H5 padding={3}>{t('housing.nextArrival')}</H5>}
                  </FlexBox>
                }
                title=""
                dontShowMoveToNextToConfirm
                setIsViewing={() => false}
                isViewing={false}
              >
                <Grid container spacing={2}>
                  <Grid item xs={nextArrival ? 7 : 12}>
                    {roommates.length > 0 ? roommates.map((rm) => {
                      const emp = (rm as any).employee;
                      const avatar = emp?.filePath || defaultProfileImage;
                      return (
                        <Grid container item xs={12} key={rm.id} sx={{ display: 'flex', alignItems: 'center', pb: 1 }}>
                          <Avatar src={avatar} alt="Avatar" sx={{ width: 30, height: 30, mr: '0.5rem' }} />
                          <Box
                            sx={{ flexGrow: 1, p: 1, cursor: 'pointer', '&:hover': { backgroundColor: theme.palette.action.hover, borderRadius: 2 } }}
                            onClick={() => emp && navigateToEmployee(emp.id)}
                          >
                            <Typography variant="body2">
                              {emp?.firstName} {emp?.lastName}
                            </Typography>
                            <Tiny color="text.secondary">
                              {formatDate(rm.startDate)} — {formatDate(rm.endDate)}
                            </Tiny>
                          </Box>
                        </Grid>
                      );
                    }) : (
                      <Typography variant="body2" color="text.secondary">{t('housing.noRoommates')}</Typography>
                    )}
                  </Grid>

                  {nextArrival && (
                    <Grid item xs={5}>
                      <Divider orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' }, position: 'absolute', height: '80%' }} />
                      <Box>
                        <FlexBox alignItems="center" gap={1} mb={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>
                            {(nextArrival as any).employee?.firstName} {(nextArrival as any).employee?.lastName}
                          </Typography>
                        </FlexBox>
                        <Typography variant="body2" color="text.secondary">
                          {t('housing.arrivesOn')} {formatDate(nextArrival.startDate)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </TabBase>
            )}
          </Box>
        );
      })}
    </>
  );
};

export default Housing;
